import { apiJson } from '../api';
import { getAccessToken } from '../auth';
import type { StoryDetail, StoryElement, StoryFilterQuery } from '../types';
import { INTERACTION_EVENT_TYPES } from '../utils/userEvents';
import type { UserEventType } from '../utils/userEvents';

export interface UserEventContext {
  surface: 'tldr' | 'search' | 'channel' | 'article' | 'topics' | 'unknown';
  surfaceId?: string | null;
}

export async function getStory(storyId: string): Promise<StoryDetail> {
  return apiJson<StoryDetail>(`/api/stories/${encodeURIComponent(storyId)}`);
}

export interface StoryPermalinkResponse {
  permalink_id: string;
  story: StoryElement;
}

export async function getStoryPermalink(identifier: string): Promise<StoryPermalinkResponse> {
  return apiJson<StoryPermalinkResponse>(
    `/api/story-groups/${encodeURIComponent(identifier)}`,
    { public: true },
  );
}

/**
 * Topic siblings for a story whose surface did not ship them inline. Fetched
 * when a reader expands a Fast Search row (issue #837) rather than for every
 * result, so the cost lands only on rows people actually open.
 */
export async function getStoryAlternates(
  storyId: string,
): Promise<NonNullable<StoryElement['alternatives']>> {
  const data = await apiJson<{ alternates?: StoryElement['alternatives'] }>(
    `/api/stories/${encodeURIComponent(storyId)}/alternates`,
    { public: !getAccessToken() },
  );
  return data.alternates || [];
}

/**
 * A story's score-breakdown blob, fetched when the breakdown modal opens. The
 * reader list defers it (see backend/app/services/reader_row_projection.py):
 * it is admin-only detail, and shipping it with every row cost a quarter of
 * the feed payload for a dialog most readers cannot open.
 */
export async function getStoryScoreDetails(
  storyId: string,
): Promise<StoryElement['base_score_details']> {
  const data = await apiJson<{ base_score_details?: StoryElement['base_score_details'] }>(
    `/api/stories/${encodeURIComponent(storyId)}/score-details`,
  );
  return data.base_score_details ?? null;
}

export async function searchStories(query: StoryFilterQuery): Promise<StoryElement[]> {
  const data = await apiJson<StoryElement[]>('/api/stories/search', {
    method: 'POST',
    json: query,
  });
  return Array.isArray(data) ? data : [];
}

/**
 * Status events remain per-account. Anonymous readers send only interaction
 * telemetry through the constrained public endpoint, which stores no user id.
 */
export async function recordEvent(
  storyId: string,
  eventType: UserEventType,
  context: UserEventContext = { surface: 'unknown' },
): Promise<void> {
  const authenticated = Boolean(getAccessToken());
  if (!authenticated && !(INTERACTION_EVENT_TYPES as readonly string[]).includes(eventType)) {
    return;
  }
  await apiJson(`${authenticated ? '/api/stories' : '/api/public/stories'}/${encodeURIComponent(storyId)}/events`, {
    method: 'POST',
    json: { event_type: eventType, surface: context.surface, surface_id: context.surfaceId || null },
    public: !authenticated,
  });
}
